import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { OutreachReplyAttachmentPicker } from '@/components/admin/OutreachReplyAttachmentPicker';
import { apiJson, ApiError } from '@/lib/api';
import { buildResendTestFormData } from '@/lib/outreachReplySend';
import { formatPhoneNumber, normalizeUsPhoneNumber } from '@/lib/phone';
import type {
  ApplySpacesCorsResponse,
  DeliverabilityTestProvider,
  IssueSignInLinkResponse,
  MarketingEmailSuppressionAdminItem,
  MarketingEmailSuppressionListResponse,
  PreWhitelistedNumberAdminItem,
  PreWhitelistedNumberListResponse,
  SendDeliverabilityTestEmailResponse,
  SendPushNotificationResponse,
  SendTestResendEmailResponse,
  SpacesCorsStatusResponse,
  BackfillPaidConversionResponse,
  TriggerConversionResponse,
} from '@/lib/types';

const DELIVERABILITY_PROVIDER_LABEL: Record<DeliverabilityTestProvider, string> =
  {
    resend: 'Resend',
    mailgun: 'Mailgun',
  };

const DELIVERABILITY_FROM_PREVIEW: Record<DeliverabilityTestProvider, string> = {
  resend: 'Harbor <hello@hello.harborcoparent.com>',
  mailgun: 'Harbor <hello@mg.harborcoparent.com>',
};

const DELIVERABILITY_DMARC_DOMAIN: Record<DeliverabilityTestProvider, string> = {
  resend: '_dmarc.hello.harborcoparent.com',
  mailgun: '_dmarc.mg.harborcoparent.com',
};

const LIST_QUERY_KEY = ['admin', 'pre-whitelisted-numbers'] as const;
const MARKETING_SUPPRESSION_QUERY_KEY = [
  'admin',
  'marketing-email-suppressions',
] as const;
const SPACES_CORS_QUERY_KEY = ['admin', 'spaces-cors'] as const;

function parseDetail(err: unknown): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const j = JSON.parse(err.bodyText) as { detail?: string | string[] };
      if (typeof j.detail === 'string') return j.detail;
      if (Array.isArray(j.detail)) return j.detail.map(String).join('; ');
    } catch {
      /* ignore */
    }
    return err.bodyText;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

/** Parse comma- or whitespace-separated user IDs; empty input means broadcast (null). */
function parseOptionalUserIds(raw: string): number[] | null {
  const parts = raw
    .split(/[\s,]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return null;
  const ids: number[] = [];
  for (const p of parts) {
    const n = Number(p);
    if (!Number.isInteger(n) || n <= 0) {
      throw new Error(`Invalid user id: "${p}"`);
    }
    ids.push(n);
  }
  return ids;
}

type ConfirmDialogState = {
  title: string;
  message: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
};

function ConfirmDialog({
  open,
  state,
  onClose,
}: {
  open: boolean;
  state: ConfirmDialogState | null;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !state) return null;

  const confirmClass = state.destructive
    ? 'bg-red-600 text-white hover:bg-red-700'
    : 'bg-nova-primary text-white hover:opacity-90';

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      <div
        className='absolute inset-0 bg-black/40'
        aria-hidden
        onClick={onClose}
      />
      <div
        className='relative z-10 bg-nova-card rounded-lg shadow-xl max-w-lg w-full p-4 space-y-4 border border-nova-border'
        role='dialog'
        aria-modal='true'
        aria-labelledby='confirm-dialog-title'
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id='confirm-dialog-title'
          className='text-lg font-semibold text-nova-foreground'
        >
          {state.title}
        </h3>
        <p className='text-sm text-nova-muted-foreground whitespace-pre-wrap'>
          {state.message}
        </p>
        <div className='flex justify-end gap-2 pt-2'>
          <button
            type='button'
            onClick={onClose}
            className='px-4 py-2 text-sm font-semibold border border-nova-input rounded-md text-nova-foreground hover:bg-nova-accent'
          >
            Cancel
          </button>
          <button
            type='button'
            onClick={() => {
              state.onConfirm();
              onClose();
            }}
            className={`px-4 py-2 text-sm font-semibold rounded-md ${confirmClass}`}
          >
            {state.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminToolsPage() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [suppressionEmail, setSuppressionEmail] = useState('');
  const [suppressionReason, setSuppressionReason] = useState('');
  const [suppressionFormError, setSuppressionFormError] = useState<string | null>(
    null
  );
  const [suppressionDeleteError, setSuppressionDeleteError] = useState<
    string | null
  >(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(
    null
  );

  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushDeeplink, setPushDeeplink] = useState('');
  const [pushUserIdsRaw, setPushUserIdsRaw] = useState('');
  const [pushBanner, setPushBanner] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);

  const [resendTestEmail, setResendTestEmail] = useState('');
  const [resendTestFiles, setResendTestFiles] = useState<File[]>([]);
  const [resendTestBanner, setResendTestBanner] = useState<{
    ok: boolean;
    text: string;
    messageId?: string | null;
    attachmentFilenames?: string[];
  } | null>(null);

  const [deliverabilityEmail, setDeliverabilityEmail] = useState('');
  const [deliverabilityProvider, setDeliverabilityProvider] =
    useState<DeliverabilityTestProvider>('resend');
  const [deliverabilityBanner, setDeliverabilityBanner] = useState<{
    ok: boolean;
    text: string;
    provider?: DeliverabilityTestProvider;
    fromEmail?: string;
    messageId?: string | null;
  } | null>(null);

  const [signInLinkPhone, setSignInLinkPhone] = useState('');
  const [signInLinkBanner, setSignInLinkBanner] = useState<{
    ok: boolean;
    text: string;
    deepLink?: string;
  } | null>(null);
  const [signInLinkCopied, setSignInLinkCopied] = useState(false);

  const [conversionUserId, setConversionUserId] = useState('');
  const [conversionBanner, setConversionBanner] = useState<{
    ok: boolean;
    text: string;
    result?: TriggerConversionResponse;
  } | null>(null);

  const [retractUserId, setRetractUserId] = useState('');
  const [retractBanner, setRetractBanner] = useState<{
    ok: boolean;
    text: string;
    result?: TriggerConversionResponse;
  } | null>(null);

  const [paidBackfillPreview, setPaidBackfillPreview] =
    useState<BackfillPaidConversionResponse | null>(null);
  const [paidBackfillBanner, setPaidBackfillBanner] = useState<{
    ok: boolean;
    text: string;
    result?: BackfillPaidConversionResponse;
  } | null>(null);

  const [spacesCorsBanner, setSpacesCorsBanner] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);

  const listQuery = useQuery({
    queryKey: LIST_QUERY_KEY,
    queryFn: () =>
      apiJson<PreWhitelistedNumberListResponse>(
        getToken,
        '/v1/admin/pre-whitelisted-numbers'
      ),
  });

  const normalized = normalizeUsPhoneNumber(phoneDisplay);
  const canSubmit = normalized.length > 0;

  const addMutation = useMutation({
    mutationFn: () =>
      apiJson<PreWhitelistedNumberAdminItem>(
        getToken,
        '/v1/admin/pre-whitelisted-numbers',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone_number: normalized }),
        }
      ),
    onSuccess: async () => {
      setFormError(null);
      setPhoneDisplay('');
      await queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY });
    },
    onError: (e) => setFormError(parseDetail(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiJson<void>(getToken, `/v1/admin/pre-whitelisted-numbers/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      setDeleteError(null);
      await queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY });
    },
    onError: (e) => setDeleteError(parseDetail(e)),
  });

  const items = listQuery.data?.items ?? [];

  const suppressionListQuery = useQuery({
    queryKey: MARKETING_SUPPRESSION_QUERY_KEY,
    queryFn: () =>
      apiJson<MarketingEmailSuppressionListResponse>(
        getToken,
        '/v1/admin/marketing-email-suppressions'
      ),
  });

  const suppressionEmailNormalized = suppressionEmail.trim().toLowerCase();
  const canAddSuppression = suppressionEmailNormalized.includes('@');

  const addSuppressionMutation = useMutation({
    mutationFn: () =>
      apiJson<MarketingEmailSuppressionAdminItem>(
        getToken,
        '/v1/admin/marketing-email-suppressions',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: suppressionEmailNormalized,
            reason: suppressionReason.trim() || null,
          }),
        }
      ),
    onSuccess: async () => {
      setSuppressionFormError(null);
      setSuppressionEmail('');
      setSuppressionReason('');
      await queryClient.invalidateQueries({
        queryKey: MARKETING_SUPPRESSION_QUERY_KEY,
      });
    },
    onError: (e) => setSuppressionFormError(parseDetail(e)),
  });

  const deleteSuppressionMutation = useMutation({
    mutationFn: (id: number) =>
      apiJson<void>(getToken, `/v1/admin/marketing-email-suppressions/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      setSuppressionDeleteError(null);
      await queryClient.invalidateQueries({
        queryKey: MARKETING_SUPPRESSION_QUERY_KEY,
      });
    },
    onError: (e) => setSuppressionDeleteError(parseDetail(e)),
  });

  const suppressionItems = suppressionListQuery.data?.items ?? [];

  const spacesCorsQuery = useQuery({
    queryKey: SPACES_CORS_QUERY_KEY,
    queryFn: () =>
      apiJson<SpacesCorsStatusResponse>(getToken, '/v1/admin/spaces-cors'),
  });

  const applySpacesCorsMutation = useMutation({
    mutationFn: () =>
      apiJson<ApplySpacesCorsResponse>(
        getToken,
        '/v1/admin/spaces-cors/apply',
        { method: 'POST' }
      ),
    onSuccess: async (data) => {
      setSpacesCorsBanner({ ok: data.success, text: data.message });
      await queryClient.invalidateQueries({ queryKey: SPACES_CORS_QUERY_KEY });
    },
    onError: (e) =>
      setSpacesCorsBanner({ ok: false, text: parseDetail(e) }),
  });

  const pushMutation = useMutation({
    mutationFn: (userIds: number[] | null) => {
      const data =
        pushDeeplink.trim().length > 0
          ? { deeplink: pushDeeplink.trim() }
          : null;
      return apiJson<SendPushNotificationResponse>(
        getToken,
        '/v1/admin/send-push-notifications',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: pushTitle.trim(),
            body: pushBody.trim(),
            data,
            user_ids: userIds,
          }),
        }
      );
    },
    onSuccess: (data) => {
      setPushBanner({ ok: data.success, text: data.message });
      if (data.success) {
        setPushTitle('');
        setPushBody('');
        setPushDeeplink('');
        setPushUserIdsRaw('');
      }
    },
    onError: (e) => setPushBanner({ ok: false, text: parseDetail(e) }),
  });

  const canSendPush = pushTitle.trim().length > 0 && pushBody.trim().length > 0;

  const resendTestMutation = useMutation({
    mutationFn: () =>
      apiJson<SendTestResendEmailResponse>(
        getToken,
        '/v1/admin/send-test-resend-email',
        {
          method: 'POST',
          body: buildResendTestFormData(
            resendTestEmail.trim(),
            resendTestFiles,
          ),
        }
      ),
    onSuccess: (data) => {
      setResendTestBanner({
        ok: data.success,
        text: data.message,
        messageId: data.resend_message_id,
        attachmentFilenames: data.attachment_filenames ?? [],
      });
    },
    onError: (e) =>
      setResendTestBanner({
        ok: false,
        text: parseDetail(e),
        messageId: null,
        attachmentFilenames: [],
      }),
  });

  const canSendResendTest = resendTestEmail.trim().length >= 3;

  const deliverabilityTestMutation = useMutation({
    mutationFn: (provider: DeliverabilityTestProvider) =>
      apiJson<SendDeliverabilityTestEmailResponse>(
        getToken,
        '/v1/admin/send-deliverability-test-email',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            provider,
            email: deliverabilityEmail.trim(),
          }),
        }
      ),
    onSuccess: (data) => {
      setDeliverabilityBanner({
        ok: data.success,
        text: data.message,
        provider: data.provider,
        fromEmail: data.from_email,
        messageId: data.message_id,
      });
    },
    onError: (e) =>
      setDeliverabilityBanner({
        ok: false,
        text: parseDetail(e),
        provider: deliverabilityProvider,
        messageId: null,
      }),
  });

  const canSendDeliverabilityTest = deliverabilityEmail.trim().length >= 3;

  const signInLinkNormalized = normalizeUsPhoneNumber(signInLinkPhone);
  const canIssueSignInLink = signInLinkNormalized.length > 0;

  const issueSignInLinkMutation = useMutation({
    mutationFn: () =>
      apiJson<IssueSignInLinkResponse>(
        getToken,
        '/v1/admin/issue-signin-link',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone_number: signInLinkNormalized }),
        }
      ),
    onSuccess: (data) => {
      setSignInLinkCopied(false);
      setSignInLinkBanner({
        ok: true,
        text: data.message,
        deepLink: data.deep_link,
      });
    },
    onError: (e) => setSignInLinkBanner({ ok: false, text: parseDetail(e) }),
  });

  const requestIssueSignInLink = () => {
    if (!canIssueSignInLink) return;
    setSignInLinkBanner(null);
    setConfirmDialog({
      title: 'Issue sign-in link?',
      message:
        `Issue a one-time sign-in link for ${signInLinkNormalized}? ` +
        `If no account exists for this number, one will be created. ` +
        `The link bypasses SMS verification and expires in 1 hour.`,
      confirmLabel: 'Issue link',
      onConfirm: () => issueSignInLinkMutation.mutate(),
    });
  };

  const conversionUserIdTrimmed = conversionUserId.trim();
  const parsedConversionUserId = (() => {
    if (conversionUserIdTrimmed.length === 0) return null; // null = all pending
    const n = Number(conversionUserIdTrimmed);
    if (!Number.isInteger(n) || n <= 0) return undefined; // invalid
    return n;
  })();
  const conversionUserIdValid = parsedConversionUserId !== undefined;

  const conversionMutation = useMutation({
    mutationFn: (userId: number | null) =>
      apiJson<TriggerConversionResponse>(
        getToken,
        '/v1/admin/trigger-conversion-upload',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId }),
        }
      ),
    onSuccess: (data) => {
      const uploaded = data.results.filter((r) => r.success).length;
      const failed = data.results.length - uploaded;
      const summary = data.success
        ? `${data.message} — ${uploaded} uploaded${
            failed > 0 ? `, ${failed} skipped/failed` : ''
          }.`
        : data.message;
      setConversionBanner({ ok: data.success, text: summary, result: data });
    },
    onError: (e) => setConversionBanner({ ok: false, text: parseDetail(e) }),
  });

  const requestRunConversion = () => {
    if (!conversionUserIdValid || conversionMutation.isPending) return;
    setConversionBanner(null);
    const userId = parsedConversionUserId ?? null;
    setConfirmDialog({
      title: 'Upload Google Ads conversions?',
      message:
        userId === null
          ? 'Upload offline conversions to Google Ads for ALL pending fingerprint-matched users with a gclid that have not been uploaded yet. ' +
            'This pending queue can include open/active trials (product id set, $0 gross). ' +
            'For missed trial\u2192paid converts, cancel and use \u201cBackfill trial\u2192paid Google uploads\u201d instead. ' +
            'Each conversion uses the user\u2019s real RevenueCat purchase price. This writes to production Google Ads and is not reversible. Continue?'
          : `Upload the offline conversion to Google Ads for user ${userId} (uses their real RevenueCat purchase price). This writes to production Google Ads. Continue?`,
      confirmLabel: 'Upload conversions',
      destructive: true,
      onConfirm: () => conversionMutation.mutate(userId),
    });
  };

  const retractUserIdTrimmed = retractUserId.trim();
  const parsedRetractUserId = (() => {
    if (retractUserIdTrimmed.length === 0) return null; // null = all false (non-payers)
    const n = Number(retractUserIdTrimmed);
    if (!Number.isInteger(n) || n <= 0) return undefined; // invalid
    return n;
  })();
  const retractUserIdValid = parsedRetractUserId !== undefined;

  const retractMutation = useMutation({
    mutationFn: (userId: number | null) =>
      apiJson<TriggerConversionResponse>(
        getToken,
        '/v1/admin/retract-conversion-upload',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId }),
        }
      ),
    onSuccess: (data) => {
      const retracted = data.results.filter((r) => r.success).length;
      const failed = data.results.length - retracted;
      const summary = data.success
        ? `${data.message} — ${retracted} retracted${
            failed > 0 ? `, ${failed} no-op/failed` : ''
          }.`
        : data.message;
      setRetractBanner({ ok: data.success, text: summary, result: data });
    },
    onError: (e) => setRetractBanner({ ok: false, text: parseDetail(e) }),
  });

  const requestRunRetraction = () => {
    if (!retractUserIdValid || retractMutation.isPending) return;
    setRetractBanner(null);
    const userId = parsedRetractUserId ?? null;
    setConfirmDialog({
      title: 'Retract Google Ads conversions?',
      message:
        userId === null
          ? 'Retract the false conversions: every fingerprint-matched user with a gclid that was marked uploaded but never actually purchased (no RevenueCat product). ' +
            'This writes a RETRACTION to production Google Ads. Conversions Google never recorded are harmless no-ops. Continue?'
          : `Retract the uploaded Google Ads conversion for user ${userId}. This writes a RETRACTION to production Google Ads. Continue?`,
      confirmLabel: 'Retract conversions',
      destructive: true,
      onConfirm: () => retractMutation.mutate(userId),
    });
  };

  const paidBackfillMutation = useMutation({
    mutationFn: (dryRun: boolean) =>
      apiJson<BackfillPaidConversionResponse>(
        getToken,
        '/v1/admin/backfill-paid-conversion-uploads',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dry_run: dryRun }),
        }
      ),
    onSuccess: (data, dryRun) => {
      if (dryRun) {
        setPaidBackfillPreview(data);
        setPaidBackfillBanner({
          ok: data.success,
          text: data.message,
          result: data,
        });
        return;
      }
      setPaidBackfillPreview(null);
      const uploaded = data.results.filter((r) => r.success).length;
      const failed = data.results.length - uploaded;
      const summary = data.success
        ? `${data.message}${failed > 0 ? ` — ${failed} failed` : ''}.`
        : data.message;
      setPaidBackfillBanner({ ok: data.success, text: summary, result: data });
    },
    onError: (e) => setPaidBackfillBanner({ ok: false, text: parseDetail(e) }),
  });

  const requestPaidBackfillPreview = () => {
    if (paidBackfillMutation.isPending) return;
    setPaidBackfillBanner(null);
    setPaidBackfillPreview(null);
    paidBackfillMutation.mutate(true);
  };

  const requestPaidBackfillUpload = () => {
    if (paidBackfillMutation.isPending || !paidBackfillPreview) return;
    const ids = paidBackfillPreview.eligible.map((c) => c.user_id);
    if (ids.length === 0) return;
    setConfirmDialog({
      title: 'Upload trial→paid Google conversions?',
      message:
        `Upload ${ids.length} actual paid convert(s) to Google Ads conversion action ` +
        `${paidBackfillPreview.conversion_action_id ?? 'GOOGLE_ADS_CONVERSION_ACTION_ID'}.\n\n` +
        `User IDs: ${ids.join(', ')}\n\n` +
        'This is not “Upload all pending” — open trials, intro, and $0 gross are excluded. ' +
        'Writes to production Google Ads. Continue?',
      confirmLabel: `Upload ${ids.length} paid convert${ids.length === 1 ? '' : 's'}`,
      destructive: true,
      onConfirm: () => paidBackfillMutation.mutate(false),
    });
  };

  const requestApplySpacesCors = () => {
    if (applySpacesCorsMutation.isPending) return;
    setSpacesCorsBanner(null);
    const bucket = spacesCorsQuery.data?.bucket ?? 'co-parent';
    setConfirmDialog({
      title: 'Apply Spaces CORS configuration?',
      message:
        `Update the CORS rules on DigitalOcean Spaces bucket "${bucket}" so browser attachment uploads work from the web app.\n\n` +
        'This allows PUT requests with a Content-Type header from harborcoparent.com (and dev localhost origins). ' +
        'Required for payment, expense, and journal attachment uploads on web.',
      confirmLabel: 'Apply CORS',
      destructive: true,
      onConfirm: () => applySpacesCorsMutation.mutate(),
    });
  };

  const requestSendPush = () => {
    if (!canSendPush) return;
    setPushBanner(null);
    let userIds: number[] | null;
    try {
      userIds = parseOptionalUserIds(pushUserIdsRaw);
    } catch (e) {
      setPushBanner({
        ok: false,
        text: e instanceof Error ? e.message : 'Invalid user IDs',
      });
      return;
    }

    const title = pushTitle.trim();
    const body = pushBody.trim();
    const deeplink = pushDeeplink.trim();
    const preview =
      `Title: ${title}\n\nBody:\n${body}` +
      (deeplink ? `\n\nDeeplink: ${deeplink}` : '') +
      (userIds === null
        ? '\n\nAudience: ALL users with a push token.'
        : `\n\nAudience: ${userIds.length} user(s) with IDs: ${userIds.join(
            ', '
          )}.`);

    setConfirmDialog({
      title: 'Send push notification?',
      message:
        userIds === null
          ? `${preview}\n\nThis cannot be undone. Are you sure?`
          : `${preview}\n\nSend to these users only?`,
      confirmLabel: 'Send push',
      destructive: userIds === null,
      onConfirm: () => pushMutation.mutate(userIds),
    });
  };

  return (
    <div className='space-y-4'>
      <h1 className='text-2xl font-semibold text-nova-foreground'>Tools</h1>

      <section className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4 space-y-4'>
        <h2 className='text-lg font-semibold text-nova-foreground'>
          Pre-whitelisted numbers
        </h2>
        <p className='text-sm text-nova-muted-foreground'>
          Numbers on this list receive subscription override when a matching
          user signs up. US numbers only (+1).
        </p>

        <form
          className='flex flex-wrap gap-3 items-end'
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit) return;
            setFormError(null);
            setConfirmDialog({
              title: 'Add pre-whitelisted number?',
              message: `Add ${normalized} to the pre-whitelist? They will receive subscription override when they sign up with this phone number.`,
              confirmLabel: 'Add number',
              onConfirm: () => addMutation.mutate(),
            });
          }}
        >
          <div className='flex-1 min-w-[220px]'>
            <label className='block text-xs font-semibold text-nova-muted-foreground mb-1'>
              Add phone number
            </label>
            <div className='flex items-center gap-2'>
              <span className='text-sm text-nova-muted-foreground select-none'>+1</span>
              <input
                type='tel'
                value={phoneDisplay}
                onChange={(e) => {
                  setPhoneDisplay(formatPhoneNumber(e.target.value));
                  setFormError(null);
                }}
                placeholder='(555) 123-4567'
                className='flex-1 min-w-0 px-3 py-2 border border-nova-input rounded-md text-sm'
              />
            </div>
          </div>
          <button
            type='submit'
            disabled={!canSubmit || addMutation.isPending}
            className='px-4 py-2 rounded-md text-sm font-semibold text-white bg-nova-primary hover:opacity-90 disabled:opacity-50'
          >
            {addMutation.isPending ? 'Adding...' : 'Add'}
          </button>
        </form>
        {formError && <p className='text-sm text-red-600'>{formError}</p>}
        {deleteError && <p className='text-sm text-red-600'>{deleteError}</p>}

        <div className='border border-nova-border rounded-md overflow-hidden'>
          <table className='w-full text-sm'>
            <thead className='bg-nova-muted text-xs uppercase text-nova-muted-foreground'>
              <tr>
                <th scope='col' className='px-4 py-3 text-left'>
                  Phone
                </th>
                <th scope='col' className='px-4 py-3 text-left'>
                  Added
                </th>
                <th scope='col' className='px-4 py-3 text-left'>
                  Status
                </th>
                <th scope='col' className='px-4 py-3 text-right'>
                  User ID
                </th>
                <th scope='col' className='px-4 py-3 text-right'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-nova-border'>
              {listQuery.isLoading && (
                <tr>
                  <td
                    colSpan={5}
                    className='px-4 py-8 text-center text-nova-muted-foreground'
                  >
                    Loading...
                  </td>
                </tr>
              )}
              {listQuery.isError && (
                <tr>
                  <td
                    colSpan={5}
                    className='px-4 py-8 text-center text-red-600'
                  >
                    {parseDetail(listQuery.error)}
                  </td>
                </tr>
              )}
              {!listQuery.isLoading &&
                !listQuery.isError &&
                items.map((row) => (
                  <tr key={row.id} className='hover:bg-nova-accent'>
                    <td className='px-4 py-2 font-mono tabular-nums'>
                      {row.phone_number}
                    </td>
                    <td className='px-4 py-2 text-nova-muted-foreground'>
                      {new Date(row.created_at).toLocaleString()}
                    </td>
                    <td className='px-4 py-2'>
                      {row.applied ? (
                        <span className='inline-flex px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-xs font-semibold'>
                          Applied
                        </span>
                      ) : (
                        <span className='inline-flex px-2 py-0.5 rounded-md bg-nova-muted text-nova-foreground text-xs font-semibold'>
                          Pending
                        </span>
                      )}
                    </td>
                    <td className='px-4 py-2 text-right tabular-nums text-nova-foreground'>
                      {row.applied_to_user_id ?? '-'}
                    </td>
                    <td className='px-4 py-2 text-right'>
                      <button
                        type='button'
                        disabled={deleteMutation.isPending}
                        onClick={() => {
                          const message = row.applied
                            ? `Remove ${row.phone_number}? It was already applied to user ${row.applied_to_user_id}.`
                            : `Remove ${row.phone_number} from the pre-whitelist?`;
                          setConfirmDialog({
                            title: 'Remove pre-whitelisted number?',
                            message,
                            confirmLabel: 'Remove',
                            destructive: true,
                            onConfirm: () => deleteMutation.mutate(row.id),
                          });
                        }}
                        className='inline-flex px-2 py-1 rounded-md border border-red-200 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50'
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              {!listQuery.isLoading &&
                !listQuery.isError &&
                items.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className='px-4 py-8 text-center text-nova-muted-foreground'
                    >
                      No pre-whitelisted numbers.
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>
      </section>

      <section className='bg-nova-card rounded-lg shadow-sm border border-amber-300 p-4 space-y-4'>
        <div className='flex flex-wrap items-center gap-2'>
          <h2 className='text-lg font-semibold text-nova-foreground'>
            Spaces attachment upload CORS
          </h2>
          <span className='inline-flex px-2 py-0.5 rounded-md bg-amber-100 text-amber-900 text-xs font-semibold'>
            Writes to prod
          </span>
          {spacesCorsQuery.data ? (
            spacesCorsQuery.data.is_adequate ? (
              <span className='inline-flex px-2 py-0.5 rounded-md bg-green-100 text-green-800 text-xs font-semibold'>
                OK
              </span>
            ) : (
              <span className='inline-flex px-2 py-0.5 rounded-md bg-red-100 text-red-800 text-xs font-semibold'>
                Needs update
              </span>
            )
          ) : null}
        </div>
        <p className='text-sm text-nova-muted-foreground'>
          Web attachment uploads PUT files directly to DigitalOcean Spaces with a{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>
            Content-Type
          </code>{' '}
          header. Browsers preflight that request; if the bucket CORS rules
          don&rsquo;t allow the header, uploads fail with &ldquo;Payment saved,
          but attachment(s) failed to upload.&rdquo; Use this tool to apply the
          required CORS rules. Hits{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>
            GET /v1/admin/spaces-cors
          </code>{' '}
          and{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>
            POST /v1/admin/spaces-cors/apply
          </code>
          .
        </p>

        {spacesCorsBanner ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              spacesCorsBanner.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {spacesCorsBanner.text}
          </div>
        ) : null}

        {spacesCorsQuery.isLoading ? (
          <p className='text-sm text-nova-muted-foreground'>Loading bucket CORS status...</p>
        ) : null}
        {spacesCorsQuery.isError ? (
          <p className='text-sm text-red-600'>
            {parseDetail(spacesCorsQuery.error)}
          </p>
        ) : null}

        {spacesCorsQuery.data ? (
          <div className='grid gap-4 lg:grid-cols-2'>
            <div>
              <p className='text-xs font-semibold text-nova-muted-foreground mb-1'>
                Current ({spacesCorsQuery.data.bucket})
              </p>
              <pre className='text-xs bg-nova-muted border border-nova-border rounded-md p-3 overflow-x-auto max-h-64'>
                {spacesCorsQuery.data.current
                  ? JSON.stringify(spacesCorsQuery.data.current, null, 2)
                  : '(none)'}
              </pre>
            </div>
            <div>
              <p className='text-xs font-semibold text-nova-muted-foreground mb-1'>
                Intended configuration
              </p>
              <pre className='text-xs bg-nova-muted border border-nova-border rounded-md p-3 overflow-x-auto max-h-64'>
                {JSON.stringify(spacesCorsQuery.data.intended, null, 2)}
              </pre>
            </div>
          </div>
        ) : null}

        <div className='flex flex-wrap gap-3 items-center'>
          <button
            type='button'
            disabled={
              applySpacesCorsMutation.isPending ||
              spacesCorsQuery.isLoading ||
              spacesCorsQuery.data?.is_adequate === true
            }
            onClick={requestApplySpacesCors}
            className='px-4 py-2 text-sm font-semibold rounded-md text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50'
          >
            {applySpacesCorsMutation.isPending
              ? 'Applying...'
              : 'Apply CORS configuration'}
          </button>
          <button
            type='button'
            disabled={spacesCorsQuery.isFetching}
            onClick={() => {
              setSpacesCorsBanner(null);
              void queryClient.invalidateQueries({
                queryKey: SPACES_CORS_QUERY_KEY,
              });
            }}
            className='px-4 py-2 text-sm font-semibold rounded-md border border-nova-input text-nova-foreground hover:bg-nova-accent disabled:opacity-50'
          >
            Refresh status
          </button>
        </div>
      </section>

      <section className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4 space-y-4'>
        <h2 className='text-lg font-semibold text-nova-foreground'>
          Issue sign-in link (OTP bypass)
        </h2>
        <p className='text-sm text-nova-muted-foreground'>
          Generates a one-time{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>
            harborcoparent://ticket
          </code>{' '}
          deep link that signs a user in without the SMS code. Use when a user
          can&rsquo;t receive their verification code (e.g. carrier/region
          delivery failure). Creates the account if it doesn&rsquo;t exist yet.
          Link expires in 1 hour &mdash; send it to the user to open on a device
          with the Harbor app installed. US/Canada numbers only (+1).
        </p>
        {signInLinkBanner ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              signInLinkBanner.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <p>{signInLinkBanner.text}</p>
            {signInLinkBanner.ok && signInLinkBanner.deepLink ? (
              <div className='mt-2 space-y-2'>
                <code className='block font-mono text-xs opacity-90 break-all bg-nova-card/70 rounded px-2 py-1 border border-green-200'>
                  {signInLinkBanner.deepLink}
                </code>
                <button
                  type='button'
                  onClick={async () => {
                    const link = signInLinkBanner.deepLink;
                    if (!link) return;
                    try {
                      await navigator.clipboard.writeText(link);
                      setSignInLinkCopied(true);
                      setTimeout(() => setSignInLinkCopied(false), 2000);
                    } catch {
                      /* clipboard unavailable; user can select manually */
                    }
                  }}
                  className='inline-flex px-3 py-1.5 rounded-md border border-green-300 text-xs font-semibold text-green-800 hover:bg-green-100'
                >
                  {signInLinkCopied ? 'Copied!' : 'Copy link'}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
        <form
          className='flex flex-wrap gap-3 items-end'
          onSubmit={(e) => {
            e.preventDefault();
            requestIssueSignInLink();
          }}
        >
          <div className='flex-1 min-w-[220px]'>
            <label className='block text-xs font-semibold text-nova-muted-foreground mb-1'>
              Phone number
            </label>
            <div className='flex items-center gap-2'>
              <span className='text-sm text-nova-muted-foreground select-none'>+1</span>
              <input
                type='tel'
                value={signInLinkPhone}
                onChange={(e) => {
                  setSignInLinkPhone(formatPhoneNumber(e.target.value));
                  setSignInLinkBanner(null);
                }}
                placeholder='(555) 123-4567'
                className='flex-1 min-w-0 px-3 py-2 border border-nova-input rounded-md text-sm'
              />
            </div>
          </div>
          <button
            type='submit'
            disabled={!canIssueSignInLink || issueSignInLinkMutation.isPending}
            className='px-4 py-2 rounded-md text-sm font-semibold text-white bg-nova-primary hover:opacity-90 disabled:opacity-50'
          >
            {issueSignInLinkMutation.isPending ? 'Issuing...' : 'Issue link'}
          </button>
        </form>
      </section>

      <section className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4 space-y-4'>
        <h2 className='text-lg font-semibold text-nova-foreground'>
          Marketing email suppressions
        </h2>
        <p className='text-sm text-nova-muted-foreground'>
          Emails on this list will not receive any future marketing
          communications (drips, retention, prospects, coach outreach, etc.).
          Transactional email such as data exports is not blocked. Adding an
          email also syncs global drip unsubscribe and prospect/coach opt-out
          state when those records exist.
        </p>

        <form
          className='flex flex-wrap gap-3 items-end'
          onSubmit={(e) => {
            e.preventDefault();
            if (!canAddSuppression) return;
            setSuppressionFormError(null);
            const reasonNote = suppressionReason.trim()
              ? `\n\nReason: ${suppressionReason.trim()}`
              : '';
            setConfirmDialog({
              title: 'Add marketing email suppression?',
              message: `Block all future marketing email to ${suppressionEmailNormalized}?${reasonNote}`,
              confirmLabel: 'Add email',
              onConfirm: () => addSuppressionMutation.mutate(),
            });
          }}
        >
          <div className='flex-1 min-w-[220px]'>
            <label className='block text-xs font-semibold text-nova-muted-foreground mb-1'>
              Email address
            </label>
            <input
              type='email'
              value={suppressionEmail}
              onChange={(e) => {
                setSuppressionEmail(e.target.value);
                setSuppressionFormError(null);
              }}
              placeholder='person@example.com'
              className='w-full px-3 py-2 border border-nova-input rounded-md text-sm'
            />
          </div>
          <div className='flex-[2] min-w-[220px]'>
            <label className='block text-xs font-semibold text-nova-muted-foreground mb-1'>
              Reason (optional)
            </label>
            <input
              type='text'
              value={suppressionReason}
              onChange={(e) => {
                setSuppressionReason(e.target.value);
                setSuppressionFormError(null);
              }}
              placeholder='Requested no marketing contact'
              className='w-full px-3 py-2 border border-nova-input rounded-md text-sm'
            />
          </div>
          <button
            type='submit'
            disabled={!canAddSuppression || addSuppressionMutation.isPending}
            className='px-4 py-2 rounded-md text-sm font-semibold text-white bg-nova-primary hover:opacity-90 disabled:opacity-50'
          >
            {addSuppressionMutation.isPending ? 'Adding...' : 'Add'}
          </button>
        </form>
        {suppressionFormError && (
          <p className='text-sm text-red-600'>{suppressionFormError}</p>
        )}
        {suppressionDeleteError && (
          <p className='text-sm text-red-600'>{suppressionDeleteError}</p>
        )}

        <div className='border border-nova-border rounded-md overflow-hidden'>
          <table className='w-full text-sm'>
            <thead className='bg-nova-muted text-xs uppercase text-nova-muted-foreground'>
              <tr>
                <th scope='col' className='px-4 py-3 text-left'>
                  Email
                </th>
                <th scope='col' className='px-4 py-3 text-left'>
                  Reason
                </th>
                <th scope='col' className='px-4 py-3 text-left'>
                  Added
                </th>
                <th scope='col' className='px-4 py-3 text-right'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-nova-border'>
              {suppressionListQuery.isLoading && (
                <tr>
                  <td
                    colSpan={4}
                    className='px-4 py-8 text-center text-nova-muted-foreground'
                  >
                    Loading...
                  </td>
                </tr>
              )}
              {suppressionListQuery.isError && (
                <tr>
                  <td
                    colSpan={4}
                    className='px-4 py-8 text-center text-red-600'
                  >
                    {parseDetail(suppressionListQuery.error)}
                  </td>
                </tr>
              )}
              {!suppressionListQuery.isLoading &&
                !suppressionListQuery.isError &&
                suppressionItems.map((row) => (
                  <tr key={row.id} className='hover:bg-nova-accent'>
                    <td className='px-4 py-2 font-mono'>{row.email}</td>
                    <td className='px-4 py-2 text-nova-muted-foreground'>
                      {row.reason ?? '-'}
                    </td>
                    <td className='px-4 py-2 text-nova-muted-foreground'>
                      {new Date(row.created_at).toLocaleString()}
                    </td>
                    <td className='px-4 py-2 text-right'>
                      <button
                        type='button'
                        disabled={deleteSuppressionMutation.isPending}
                        onClick={() => {
                          setConfirmDialog({
                            title: 'Remove marketing email suppression?',
                            message: `Remove ${row.email} from the marketing suppression list? They may receive marketing email again. Existing drip/prospect/coach opt-outs are not reversed.`,
                            confirmLabel: 'Remove',
                            destructive: true,
                            onConfirm: () =>
                              deleteSuppressionMutation.mutate(row.id),
                          });
                        }}
                        className='inline-flex px-2 py-1 rounded-md border border-red-200 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50'
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              {!suppressionListQuery.isLoading &&
                !suppressionListQuery.isError &&
                suppressionItems.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className='px-4 py-8 text-center text-nova-muted-foreground'
                    >
                      No marketing email suppressions.
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>
      </section>

      <section className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4 space-y-4'>
        <h2 className='text-lg font-semibold text-nova-foreground'>
          Push notifications
        </h2>
        <p className='text-sm text-nova-muted-foreground'>
          Uses{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>
            POST /v1/admin/send-push-notifications
          </code>
          . Leave user IDs empty to target everyone with a registered push token
          (requires confirmation).
        </p>
        {pushBanner ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              pushBanner.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {pushBanner.text}
          </div>
        ) : null}
        <div className='space-y-3'>
          <input
            value={pushTitle}
            onChange={(e) => {
              setPushTitle(e.target.value);
              setPushBanner(null);
            }}
            placeholder='Title'
            className='w-full px-3 py-2 border border-nova-input rounded-md text-sm'
          />
          <textarea
            value={pushBody}
            onChange={(e) => {
              setPushBody(e.target.value);
              setPushBanner(null);
            }}
            placeholder='Body'
            rows={3}
            className='w-full px-3 py-2 border border-nova-input rounded-md text-sm'
          />
          <input
            value={pushDeeplink}
            onChange={(e) => {
              setPushDeeplink(e.target.value);
              setPushBanner(null);
            }}
            placeholder='Deeplink / data.deeplink (optional)'
            className='w-full px-3 py-2 border border-nova-input rounded-md text-sm'
          />
          <div>
            <label className='block text-xs font-semibold text-nova-muted-foreground mb-1'>
              User IDs (optional)
            </label>
            <input
              value={pushUserIdsRaw}
              onChange={(e) => {
                setPushUserIdsRaw(e.target.value);
                setPushBanner(null);
              }}
              placeholder='e.g. 12, 34 - leave empty for all users with a token'
              className='w-full px-3 py-2 border border-nova-input rounded-md text-sm'
            />
          </div>
          <button
            type='button'
            disabled={!canSendPush || pushMutation.isPending}
            onClick={requestSendPush}
            className='px-4 py-2 text-sm font-semibold bg-nova-primary text-white rounded-md disabled:opacity-50'
          >
            {pushMutation.isPending ? 'Sending...' : 'Send push'}
          </button>
        </div>
      </section>

      <section className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4 space-y-4'>
        <h2 className='text-lg font-semibold text-nova-foreground'>
          Resend test email
        </h2>
        <p className='text-sm text-nova-muted-foreground'>
          Sends via the same Resend attachment path as county / lawyer / coach
          outreach replies (
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>
            POST /v1/admin/send-test-resend-email
          </code>
          ). Use this to send the leave-behind PDFs to yourself before replying
          to a clerk.
        </p>

        {resendTestBanner ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              resendTestBanner.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <p>{resendTestBanner.text}</p>
            {resendTestBanner.ok && resendTestBanner.messageId ? (
              <p className='mt-1 font-mono text-xs opacity-90 break-all'>
                message_id: {resendTestBanner.messageId}
              </p>
            ) : null}
            {resendTestBanner.ok &&
            resendTestBanner.attachmentFilenames?.length ? (
              <p className='mt-1 text-xs opacity-90'>
                Attached {resendTestBanner.attachmentFilenames.join(', ')}.
              </p>
            ) : null}
          </div>
        ) : null}

        <div className='space-y-3'>
          <div>
            <label className='block text-xs font-semibold text-nova-muted-foreground mb-1'>
              Recipient email
            </label>
            <input
              type='email'
              autoComplete='email'
              value={resendTestEmail}
              onChange={(e) => {
                setResendTestEmail(e.target.value);
                setResendTestBanner(null);
              }}
              placeholder='you@example.com'
              className='w-full px-3 py-2 border border-nova-input rounded-md text-sm'
            />
          </div>
          <OutreachReplyAttachmentPicker
            files={resendTestFiles}
            onChange={setResendTestFiles}
            disabled={resendTestMutation.isPending}
          />
          <button
            type='button'
            disabled={!canSendResendTest || resendTestMutation.isPending}
            onClick={() => {
              setResendTestBanner(null);
              resendTestMutation.mutate();
            }}
            className='px-4 py-2 text-sm font-semibold bg-nova-primary text-white rounded-md disabled:opacity-50'
          >
            {resendTestMutation.isPending
              ? 'Sending...'
              : resendTestFiles.length > 0
                ? `Send via Resend (${resendTestFiles.length} attached)`
                : 'Send via Resend'}
          </button>
        </div>
      </section>

      <section className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4 space-y-4'>
        <h2 className='text-lg font-semibold text-nova-foreground'>
          Deliverability test email
        </h2>
        <p className='text-sm text-nova-muted-foreground'>
          Sends a short HTML test via{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>
            POST /v1/admin/send-deliverability-test-email
          </code>
          . The From: address is pinned per-provider so DMARC alignment is
          tested against the matching subdomain. Open the message in Gmail and
          use &ldquo;Show original&rdquo; to verify SPF, DKIM, and DMARC all
          PASS.
        </p>

        <div>
          <label className='block text-xs font-semibold text-nova-muted-foreground mb-2'>
            Provider
          </label>
          <div
            className='inline-flex rounded-md border border-nova-input overflow-hidden'
            role='radiogroup'
            aria-label='Email provider'
          >
            {(['resend', 'mailgun'] as DeliverabilityTestProvider[]).map(
              (p) => {
                const active = deliverabilityProvider === p;
                return (
                  <button
                    key={p}
                    type='button'
                    role='radio'
                    aria-checked={active}
                    onClick={() => {
                      setDeliverabilityProvider(p);
                      setDeliverabilityBanner(null);
                    }}
                    className={`px-4 py-2 text-sm font-semibold ${
                      active
                        ? 'bg-nova-primary text-white'
                        : 'bg-nova-card text-nova-foreground hover:bg-nova-accent'
                    }`}
                  >
                    {DELIVERABILITY_PROVIDER_LABEL[p]}
                  </button>
                );
              }
            )}
          </div>
          <p className='mt-2 text-xs text-nova-muted-foreground'>
            From:{' '}
            <code className='bg-nova-muted px-1 py-0.5 rounded'>
              {DELIVERABILITY_FROM_PREVIEW[deliverabilityProvider]}
            </code>{' '}
            — tests{' '}
            <code className='bg-nova-muted px-1 py-0.5 rounded'>
              {DELIVERABILITY_DMARC_DOMAIN[deliverabilityProvider]}
            </code>
          </p>
        </div>

        {deliverabilityBanner ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              deliverabilityBanner.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <p>{deliverabilityBanner.text}</p>
            {deliverabilityBanner.fromEmail ? (
              <p className='mt-1 font-mono text-xs opacity-90 break-all'>
                from: {deliverabilityBanner.fromEmail}
              </p>
            ) : null}
            {deliverabilityBanner.ok && deliverabilityBanner.messageId ? (
              <p className='mt-1 font-mono text-xs opacity-90 break-all'>
                message_id: {deliverabilityBanner.messageId}
              </p>
            ) : null}
          </div>
        ) : null}
        <div className='flex flex-wrap gap-3 items-end'>
          <div className='flex-1 min-w-[220px]'>
            <label className='block text-xs font-semibold text-nova-muted-foreground mb-1'>
              Recipient email
            </label>
            <input
              type='email'
              autoComplete='email'
              value={deliverabilityEmail}
              onChange={(e) => {
                setDeliverabilityEmail(e.target.value);
                setDeliverabilityBanner(null);
              }}
              placeholder='you@example.com'
              className='w-full px-3 py-2 border border-nova-input rounded-md text-sm'
            />
          </div>
          <button
            type='button'
            disabled={
              !canSendDeliverabilityTest ||
              deliverabilityTestMutation.isPending
            }
            onClick={() => {
              setDeliverabilityBanner(null);
              deliverabilityTestMutation.mutate(deliverabilityProvider);
            }}
            className='px-4 py-2 text-sm font-semibold bg-nova-primary text-white rounded-md disabled:opacity-50'
          >
            {deliverabilityTestMutation.isPending
              ? 'Sending...'
              : `Send via ${DELIVERABILITY_PROVIDER_LABEL[deliverabilityProvider]}`}
          </button>
        </div>
      </section>

      <section className='bg-nova-card rounded-lg shadow-sm border border-amber-300 p-4 space-y-4'>
        <div className='flex flex-wrap items-center gap-2'>
          <h2 className='text-lg font-semibold text-nova-foreground'>
            Google Ads conversion backfill
          </h2>
          <span className='inline-flex px-2 py-0.5 rounded-md bg-amber-100 text-amber-900 text-xs font-semibold'>
            Writes to prod
          </span>
        </div>
        <p className='text-sm text-nova-muted-foreground'>
          Uploads offline conversions to Google Ads for fingerprint-matched iOS
          users (sources{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>
            fingerprint*
          </code>
          ) that have a{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>gclid</code>{' '}
          but were never uploaded. Each conversion is sent only when RevenueCat
          paid gross is &gt; 0 (no $9.99 fallback; trials and $0 are skipped).
          Idempotent &mdash; already-uploaded users are skipped. Leave the field
          empty to process all pending, or enter a single user ID to upload just
          one. Hits{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>
            POST /v1/admin/trigger-conversion-upload
          </code>
          .
        </p>
        <p className='text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2'>
          <strong>Do not use &ldquo;Upload all pending&rdquo; for missed
          trial→paid converts.</strong>{' '}
          That queue keys off a RevenueCat product identifier, so it can include
          open/active trials. Use{' '}
          <strong>Backfill trial→paid Google uploads</strong> below — it
          previews first and only uploads RC gross &gt; 0 payers.
        </p>
        {conversionBanner ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              conversionBanner.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <p>{conversionBanner.text}</p>
            {conversionBanner.result &&
            conversionBanner.result.results.length > 0 ? (
              <div className='mt-2 space-y-1'>
                {conversionBanner.result.results.map((r) => (
                  <p
                    key={`${r.user_id}-${r.gclid ?? 'none'}`}
                    className='font-mono text-xs opacity-90 break-all'
                  >
                    {r.success ? '✓' : '✗'} user {r.user_id}: {r.message}
                  </p>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className='flex flex-wrap gap-3 items-end'>
          <div>
            <label className='block text-xs font-semibold text-nova-muted-foreground mb-1'>
              User ID (optional)
            </label>
            <input
              type='text'
              inputMode='numeric'
              value={conversionUserId}
              onChange={(e) => {
                setConversionUserId(e.target.value);
                setConversionBanner(null);
              }}
              placeholder='Leave empty for all pending'
              className='w-56 px-3 py-2 border border-nova-input rounded-md text-sm tabular-nums'
            />
            {!conversionUserIdValid ? (
              <p className='mt-1 text-xs text-red-600'>
                Enter a positive integer or leave empty.
              </p>
            ) : null}
          </div>
          <button
            type='button'
            disabled={!conversionUserIdValid || conversionMutation.isPending}
            onClick={requestRunConversion}
            className='px-4 py-2 text-sm font-semibold rounded-md text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50'
          >
            {conversionMutation.isPending
              ? 'Uploading...'
              : parsedConversionUserId
                ? `Upload for user ${parsedConversionUserId}`
                : 'Upload all pending'}
          </button>
        </div>
      </section>

      <section className='bg-nova-card rounded-lg shadow-sm border border-emerald-300 p-4 space-y-4'>
        <div className='flex flex-wrap items-center gap-2'>
          <h2 className='text-lg font-semibold text-nova-foreground'>
            Backfill trial→paid Google uploads
          </h2>
          <span className='inline-flex px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-900 text-xs font-semibold'>
            Paid converts only
          </span>
          <span className='inline-flex px-2 py-0.5 rounded-md bg-amber-100 text-amber-900 text-xs font-semibold'>
            Writes to prod after confirm
          </span>
        </div>
        <p className='text-sm text-nova-muted-foreground'>
          Finds fingerprint-matched iOS users with a real{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>gclid</code>{' '}
          whose Google conversion was never uploaded, then keeps only{' '}
          <strong>actual payers</strong> — RevenueCat paid gross &gt; 0 after
          a non-trial / non-intro period. Open trials, intro prices, and $0
          gross are skipped. This is the safe backfill for converts that
          happened while offline uploads were off. Targets{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>
            GOOGLE_ADS_CONVERSION_ACTION_ID
          </code>{' '}
          (App Subscription Import{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>
            7537427016
          </code>
          ), not the non-biddable iOS Subscription Offline action. Preview
          first; confirm only uploads the listed user IDs. Hits{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>
            POST /v1/admin/backfill-paid-conversion-uploads
          </code>
          .
        </p>
        {paidBackfillBanner ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              paidBackfillBanner.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <p>{paidBackfillBanner.text}</p>
            {paidBackfillBanner.result ? (
              <div className='mt-2 space-y-2'>
                <p className='text-xs'>
                  Scanned {paidBackfillBanner.result.scanned} pending
                  fingerprint row{paidBackfillBanner.result.scanned === 1 ? '' : 's'}
                  {' · '}
                  {paidBackfillBanner.result.eligible_count} eligible
                  {' · '}
                  {paidBackfillBanner.result.skipped_count} skipped
                  {paidBackfillBanner.result.conversion_action_id
                    ? ` · action ${paidBackfillBanner.result.conversion_action_id}`
                    : ''}
                </p>
                {paidBackfillBanner.result.eligible.length > 0 ? (
                  <div className='space-y-1'>
                    <p className='text-xs font-semibold'>
                      Eligible user IDs (will be uploaded on confirm)
                    </p>
                    {paidBackfillBanner.result.eligible.map((c) => (
                      <p
                        key={`${c.user_id}-${c.gclid ?? 'none'}`}
                        className='font-mono text-xs opacity-90 break-all'
                      >
                        user {c.user_id}: gross ${c.rc_gross.toFixed(2)}
                        {c.conversion_value != null
                          ? ` · value $${c.conversion_value.toFixed(2)}`
                          : ''}
                        {c.period_type ? ` · ${c.period_type}` : ''}
                      </p>
                    ))}
                  </div>
                ) : null}
                {paidBackfillBanner.result.skipped.length > 0 ? (
                  <div className='space-y-1'>
                    <p className='text-xs font-semibold'>Skipped</p>
                    {paidBackfillBanner.result.skipped.map((c) => (
                      <p
                        key={`${c.user_id}-${c.skip_reason ?? 'skip'}`}
                        className='font-mono text-xs opacity-90 break-all'
                      >
                        user {c.user_id}: {c.skip_reason ?? 'skipped'}
                      </p>
                    ))}
                  </div>
                ) : null}
                {paidBackfillBanner.result.results.length > 0 ? (
                  <div className='space-y-1'>
                    <p className='text-xs font-semibold'>Upload results</p>
                    {paidBackfillBanner.result.results.map((r) => (
                      <p
                        key={`${r.user_id}-${r.gclid ?? 'none'}`}
                        className='font-mono text-xs opacity-90 break-all'
                      >
                        {r.success ? '✓' : '✗'} user {r.user_id}: {r.message}
                      </p>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className='flex flex-wrap gap-3 items-end'>
          <button
            type='button'
            disabled={paidBackfillMutation.isPending}
            onClick={requestPaidBackfillPreview}
            className='px-4 py-2 text-sm font-semibold rounded-md text-emerald-900 bg-emerald-100 hover:bg-emerald-200 disabled:opacity-50'
          >
            {paidBackfillMutation.isPending &&
            paidBackfillMutation.variables === true
              ? 'Previewing...'
              : 'Preview trial→paid converts'}
          </button>
          <button
            type='button'
            disabled={
              paidBackfillMutation.isPending ||
              !paidBackfillPreview ||
              paidBackfillPreview.eligible_count === 0
            }
            onClick={requestPaidBackfillUpload}
            className='px-4 py-2 text-sm font-semibold rounded-md text-white bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50'
          >
            {paidBackfillMutation.isPending &&
            paidBackfillMutation.variables === false
              ? 'Uploading...'
              : paidBackfillPreview && paidBackfillPreview.eligible_count > 0
                ? `Upload ${paidBackfillPreview.eligible_count} trial→paid convert${
                    paidBackfillPreview.eligible_count === 1 ? '' : 's'
                  }`
                : 'Upload trial→paid converts'}
          </button>
        </div>
        <p className='text-xs text-nova-muted-foreground'>
          Confirm is disabled until a preview returns eligible user IDs. It
          never dumps the pending-trials queue.
        </p>
      </section>

      <section className='bg-nova-card rounded-lg shadow-sm border border-red-300 p-4 space-y-4'>
        <div className='flex flex-wrap items-center gap-2'>
          <h2 className='text-lg font-semibold text-nova-foreground'>
            Retract false Google Ads conversions
          </h2>
          <span className='inline-flex px-2 py-0.5 rounded-md bg-red-100 text-red-900 text-xs font-semibold'>
            Writes to prod
          </span>
        </div>
        <p className='text-sm text-nova-muted-foreground'>
          Sends a{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>
            RETRACTION
          </code>{' '}
          to Google Ads for conversions that should never have been uploaded
          &mdash; fingerprint-matched users with a{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>gclid</code>{' '}
          that were marked uploaded but never actually purchased (no RevenueCat
          product). Each conversion is matched by gclid + its original
          conversion time. Conversions Google never recorded (e.g. a gclid that
          was too old) are harmless no-ops. Leave the field empty to retract the
          whole false set, or enter a single user ID &mdash; e.g. start with{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>1037</code>{' '}
          (a false conversion inside the 90-day window) as a single-user test
          before retracting the whole set. Hits{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>
            POST /v1/admin/retract-conversion-upload
          </code>
          .
        </p>
        {retractBanner ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              retractBanner.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <p>{retractBanner.text}</p>
            {retractBanner.result &&
            retractBanner.result.results.length > 0 ? (
              <div className='mt-2 space-y-1'>
                {retractBanner.result.results.map((r) => (
                  <p
                    key={`${r.user_id}-${r.gclid ?? 'none'}`}
                    className='font-mono text-xs opacity-90 break-all'
                  >
                    {r.success ? '✓' : '✗'} user {r.user_id}: {r.message}
                  </p>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className='flex flex-wrap gap-3 items-end'>
          <div>
            <label className='block text-xs font-semibold text-nova-muted-foreground mb-1'>
              User ID (optional)
            </label>
            <input
              type='text'
              inputMode='numeric'
              value={retractUserId}
              onChange={(e) => {
                setRetractUserId(e.target.value);
                setRetractBanner(null);
              }}
              placeholder='Leave empty for the whole false set'
              className='w-56 px-3 py-2 border border-nova-input rounded-md text-sm tabular-nums'
            />
            {!retractUserIdValid ? (
              <p className='mt-1 text-xs text-red-600'>
                Enter a positive integer or leave empty.
              </p>
            ) : null}
          </div>
          <button
            type='button'
            disabled={!retractUserIdValid || retractMutation.isPending}
            onClick={requestRunRetraction}
            className='px-4 py-2 text-sm font-semibold rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50'
          >
            {retractMutation.isPending
              ? 'Retracting...'
              : parsedRetractUserId
                ? `Retract for user ${parsedRetractUserId}`
                : 'Retract false set'}
          </button>
        </div>
      </section>

      <ConfirmDialog
        open={confirmDialog !== null}
        state={confirmDialog}
        onClose={() => setConfirmDialog(null)}
      />
    </div>
  );
}

export const Route = createFileRoute('/admin/tools')({
  component: AdminToolsPage,
});
